import {
  BookA,
  BookOpen,
  Compass,
  Dices,
  House,
  Library,
  type LucideIcon,
  MessageCircleQuestion,
  MessageSquareText,
  Pencil,
  Search,
  Shield,
  Target,
  User,
} from "lucide-react";

/** A melléklet menüjének egyetlen forrása (design-rendszer 6., „Alsó navigáció",
 *  „Több-panel", „Felső sáv"): a kezdőlap csempéi, a mobil alsó sáv + „Több" panel és az
 *  asztali felső sáv + „Több" menü mind innen épül — mobilon és asztalon ugyanaz a
 *  szerkezet. Új funkció-oldalt ide kell felvenni. */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Csak a pontos útvonalon aktív (a kezdőlapé — különben minden aloldalon világítana). */
  end?: boolean;
}

/** A „Mai gyakorlat" nem link: a `useOpenDaily()` húz vagy megnyit. */
export interface DailyEntry {
  daily: true;
  label: string;
  icon: LucideIcon;
}

export type NavEntry = NavItem | DailyEntry;

export const isDaily = (entry: NavEntry): entry is DailyEntry => "daily" in entry;

export const HOME: NavItem = { to: "/app/book", label: "Kezdőlap", icon: House, end: true };
/** A fejezet-listának nincs külön oldala: a router a kezdőlap `#fejezetek` szakaszára
 *  irányít; a fejezet-oldalakon (`/fejezetek/:id`) viszont ez a pont aktív. */
export const CHAPTERS: NavItem = { to: "/app/book/fejezetek", label: "Fejezetek", icon: BookOpen };
export const EXERCISES: NavItem = { to: "/app/book/gyakorlatok", label: "Gyakorlattár", icon: Target };
export const DAILY: DailyEntry = { daily: true, label: "Mai gyakorlat", icon: Dices };
export const EXPERIENCES: NavItem = {
  to: "/app/book/tapasztalatok",
  label: "Tapasztalatok",
  icon: MessageSquareText,
};
export const GLOSSARY: NavItem = { to: "/app/book/fogalomtar", label: "Fogalomtár", icon: BookA };
/** „Ajánló” (2026-09-12 óta; korábban „Olvasnivalók”): ajánlott olvasnivalók, videók,
 *  podcastok. A kódban, az API-ban és a DB-ben a név marad `readings`. */
export const READINGS: NavItem = { to: "/app/book/ajanlo", label: "Ajánló", icon: Library };
export const ERRATA: NavItem = { to: "/app/book/hibajegyzek", label: "Hibajegyzék", icon: Pencil };
export const SEARCH: NavItem = { to: "/app/book/kereses", label: "Keresés", icon: Search };
export const PROFILE: NavItem = { to: "/app/book/profil", label: "Profil", icon: User };
/** Csak adminnak — mindenhol az utolsó menüpont. */
export const ADMIN: NavItem = { to: "/admin", label: "Admin", icon: Shield };

/** Az AI-fázis pontjai (rendszerterv 10.3): már látszanak, „hamarosan" címkével, nem
 *  kattinthatók. Élesítéskor `NavItem` lesz belőlük a megfelelő csoportban. */
export const SOON: { label: string; icon: LucideIcon }[] = [
  { label: "Kérdezz a könyvtől", icon: MessageCircleQuestion },
  { label: "Gyakorlat-ajánló", icon: Compass },
];

/** Kezdőlap „FUNKCIÓK" rácsa (a Mai gyakorlat csempéje külön, elsőként). */
export const HOME_TILES: NavItem[] = [EXERCISES, READINGS, EXPERIENCES, GLOSSARY, ERRATA, SEARCH];

/** A négy fő fül — mobilon az alsó sáv, asztalon a felső sáv (2026-10-18 óta; előtte bal
 *  oldalsáv); az ötödik mindkettőn a „Több". A rövidebb „Gyakorlatok" felirat az alsó sáv
 *  miatt — ötnél több elemnél 360 px-en összeérnének. A negyedik fül 2026-09-12 óta az
 *  Ajánló (a Fogalomtár helyett — az a Több-panelbe került). */
export const PRIMARY: NavItem[] = [
  HOME,
  { ...EXERCISES, label: "Gyakorlatok" },
  EXPERIENCES,
  READINGS,
];

/** A „Több" rácsa — mobilon panel, asztalon lenyíló menü (utána az AI-szakasz, adminnak az
 *  Admin csempe). */
export const MORE: NavEntry[] = [CHAPTERS, GLOSSARY, DAILY, ERRATA, SEARCH, PROFILE];

/** Aktív-e a „Több" fül: ha a panelben lévő oldalak egyikén (vagy aloldalán) állunk. */
export function isMoreActive(pathname: string): boolean {
  return MORE.filter((entry): entry is NavItem => !isDaily(entry)).some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}
